import { useState } from 'react'
import { useTranslation, Trans } from 'react-i18next'

import { ConfirmationResultModal, Alert, Spacer, Text } from '@percent/lemonade'
import { useMutation } from '@percent/utility'
import { useServices } from '@percent/admin-dashboard/containers/service/ServiceContext'
import type { DeleteStaticLinkModalProps } from './DeleteStaticLinkModal.types'

type ModalState = 'failure' | 'success' | undefined

export function DeleteStaticLinkModal({
  isOpen,
  onClose,
  staticLinkId,
  staticLinkSlug,
  onSuccess
}: DeleteStaticLinkModalProps) {
  const { t } = useTranslation()
  const { adminService } = useServices()
  const [modalState, setModalState] = useState<ModalState>()

  const [{ isPending }, { apiFunc: deleteStaticLink }] = useMutation(
    adminService.commands.deleteStaticLink,
    () => setModalState('success'),
    () => setModalState('failure')
  )

  const handleDelete = () => {
    deleteStaticLink({ id: staticLinkId })
  }

  const handleClose = () => {
    if (!isPending) {
      setModalState(undefined)
      onClose()
    }
  }

  const handleSuccessClose = () => {
    setModalState(undefined)
    onSuccess?.()
    onClose()
  }

  const handleFailureClose = () => {
    setModalState(undefined)
    onClose()
  }

  if (!isOpen) {
    return null
  }

  return (
    <ConfirmationResultModal
      isOpen={isOpen}
      state={modalState}
      title={t('modal.deleteStaticLink.title')}
      primaryButtonText={isPending ? t('modal.deleteStaticLink.button.deleting') : t('button.delete')}
      secondaryButtonText={t('button.cancel')}
      onConfirm={handleDelete}
      onClose={modalState === 'success' ? handleSuccessClose : handleClose}
      onFailureClose={handleFailureClose}
      loading={isPending}
      primaryBtnTestId="delete-static-link-submit-button"
      secondaryBtnTestId="delete-static-link-cancel-button"
      success={{
        title: t('modal.deleteStaticLink.success.title'),
        description: (
          <Trans
            i18nKey="modal.deleteStaticLink.success.description"
            values={{ slug: staticLinkSlug }}
            components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
          />
        ),
        button: t('button.close')
      }}
      failure={{
        title: t('modal.deleteStaticLink.error.title'),
        description: (
          <Trans
            i18nKey="modal.deleteStaticLink.error.description"
            values={{ slug: staticLinkSlug }}
            components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
          />
        ),
        button: t('button.close')
      }}
    >
      <Alert variant="warning" title={t('modal.deleteStaticLink.alert')} />
      <Spacer size={4} axis="vertical" />
      <Trans
        i18nKey="modal.deleteStaticLink.description"
        values={{ slug: staticLinkSlug }}
        components={{ bold: <Text as="span" fontWeight="semiBold" /> }}
      />
    </ConfirmationResultModal>
  )
}
